import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import { HexAlphaColorPicker, HexColorPicker } from 'react-colorful'

import useHandleClickOutside from '../../hooks/useHandleClickOutside'

import { Link, LINK_VARIANT_EXTRALIGHT } from '../link'

import {
  INPUT_COLOR_PICKER_HEX,
  INPUT_COLOR_PICKER_HEXALPHA,
  INPUT_COLOR_VARIANT_DANGER,
  INPUT_COLOR_VARIANT_DEFAULT
} from './constants'

import {
  ColorPickerContainer,
  ColorPickerInput,
  ColorPickerPopupContainer,
  ColorPickerPopupControls,
  ColorPickerPreview,
  ColorPickerPreviewContainer,
  transparentBackground
} from './InputColor.styles'

const hexColorRe = /^(#[0-9a-fA-F]{6})|(transparent)$/
const hexAlphaColorRe = /^(#[0-9a-fA-F]{8})$/

export const validateHexColorValue = v => hexColorRe.test(v)
export const validateHexAlphaColorValue = v => hexAlphaColorRe.test(v)

export const InputColor = ({
  //  placeholder,
  className,
  disabled,
  variant,
  onBlur,
  onKeyUp,
  'data-mu-test': dataAttributeTest,
  name,
  id,
  cancelLabel,
  value,
  onChange,
  picker
}) => {
  const [showPopup, setShowPopup] = useState(false)
  const [prevValue, setPrevValue] = useState('')
  const el = useRef(null)

  const inputName = name && `input-${name}`
  const inputId = id || inputName

  const { current: handleChange } = useRef(({ target: { value } }) => {
    onChange(value)
  })

  const { current: handlePickerChange } = useRef(value => {
    if (validateHexColorValue(value)) {
      onChange(value)
    } else {
      onChange('transparent')
    }
  })

  const { current: handleHexAlphaPickerChange } = useRef(value => {
    if (validateHexAlphaColorValue(value)) {
      onChange(value)
    }
  })

  const openPopup = () => {
    setPrevValue(value)
    setShowPopup(true)
  }

  const { current: closePopup } = useRef(() => setShowPopup(false))

  const handleCancel = () => {
    onChange(prevValue)
  }

  const { current: handleTransparentClick } = useRef(() => {
    onChange('transparent')
  })

  useHandleClickOutside(el, closePopup)

  const previewStyle =
    value === 'transparent' ? transparentBackground : { backgroundColor: value }

  useEffect(() => {
    if (disabled) {
      closePopup()
    }
  }, [disabled])

  return (
    <ColorPickerContainer
      className={classnames(
        'e-input-color',
        {
          'e-input-color--disabled': disabled,
          'e-input-color--danger': variant === INPUT_COLOR_VARIANT_DANGER
        },
        className
      )}
      $variant={variant}
      ref={el}
      $disabled={disabled}
    >
      <ColorPickerInput
        type='text'
        disabled={disabled}
        //  placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        onKeyUp={onKeyUp}
        name={inputName}
        id={inputId}
        data-mu-test={classnames('input', dataAttributeTest)}
      />
      <ColorPickerPreviewContainer>
        <ColorPickerPreview
          $disabled={disabled}
          style={previewStyle}
          onClick={openPopup}
        />
      </ColorPickerPreviewContainer>

      <ColorPickerPopupContainer $show={showPopup}>
        {picker === INPUT_COLOR_PICKER_HEX && (
          <HexColorPicker color={value} onChange={handlePickerChange} />
        )}
        {picker === INPUT_COLOR_PICKER_HEXALPHA && (
          <HexAlphaColorPicker
            color={value}
            onChange={handleHexAlphaPickerChange}
          />
        )}

        <ColorPickerPopupControls>
          <Link variant={LINK_VARIANT_EXTRALIGHT} onClick={handleCancel}>
            {cancelLabel}
          </Link>
          {picker === INPUT_COLOR_PICKER_HEX && (
            <ColorPickerPreview
              onClick={handleTransparentClick}
              style={transparentBackground}
            />
          )}
        </ColorPickerPopupControls>
      </ColorPickerPopupContainer>
    </ColorPickerContainer>
  )
}

InputColor.defaultProps = {
  variant: INPUT_COLOR_VARIANT_DEFAULT,
  cancelLabel: 'Cancel',
  picker: INPUT_COLOR_PICKER_HEX
}

InputColor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  //  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  onKeyUp: PropTypes.func,
  'data-mu-test': PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  cancelLabel: PropTypes.node,

  variant: PropTypes.oneOf([
    INPUT_COLOR_VARIANT_DEFAULT,
    INPUT_COLOR_VARIANT_DANGER
  ]),
  picker: PropTypes.oneOf([INPUT_COLOR_PICKER_HEX, INPUT_COLOR_PICKER_HEXALPHA])
}
